import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  CircularProgress,
  Tabs,
  Tab,
  Divider,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Snackbar,
  Alert
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import BiotechIcon from '@mui/icons-material/Biotech';
import UploadIcon from '@mui/icons-material/Upload';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DescriptionIcon from '@mui/icons-material/Description';
import ArchitectureIcon from '@mui/icons-material/Architecture';
import {
  getProject,
  updateProject,
  deleteProject,
  getUsers,
  assignUserToProject,
  removeUserFromProject,
  uploadProjectDocument,
  deleteProjectDocument
} from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import ProjectDocumentsPanel from '../components/ProjectDocumentsPanel';
import { processProject, queryProject, generateFromSOW } from '../utils/aiService';

// TabPanel component
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`project-tabpanel-${index}`}
      aria-labelledby={`project-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ProjectDetailPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const [tabValue, setTabValue] = useState(0);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [availableUsers, setAvailableUsers] = useState([]);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiQuery, setAiQuery] = useState('');
  const [aiResult, setAiResult] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });
  
  const fetchProject = async () => {
    try {
      setLoading(true);
      const response = await getProject(projectId);
      if (response.success) {
        setProject(response.project);
        // Initialize form data
        setFormData({
          name: response.project.name,
          description: response.project.description || '',
          industry_vertical: response.project.industry_vertical || '',
          module_details: response.project.module_details || '',
          implementation_scope: response.project.implementation_scope || ''
        });
      } else {
        console.error('Error fetching project:', response.message);
        showSnackbar('Failed to load project', 'error');
      }
    } catch (error) {
      console.error('Error fetching project:', error);
      showSnackbar('An error occurred while loading the project', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchAvailableUsers = async () => {
    try {
      const response = await getUsers();
      if (response.success) {
        setAvailableUsers(response.users);
      } else {
        console.error('Error fetching users:', response.message);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };
  
  useEffect(() => {
    fetchProject();
    if (currentUser && currentUser.role === 'admin') {
      fetchAvailableUsers();
    }
  }, [projectId, currentUser]);
  
  const handleChangeTab = (event, newValue) => {
    setTabValue(newValue);
  };
  
  const handleOpenEditDialog = () => {
    setEditDialogOpen(true);
  };
  
  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  const handleSubmitEdit = async () => {
    try {
      // First update the project basic details
      const projectDetails = {
        name: formData.name,
        description: formData.description,
        industry_vertical: formData.industry_vertical,
        module_details: formData.module_details,
        implementation_scope: formData.implementation_scope
      };
      
      const response = await updateProject(projectId, projectDetails);
      
      if (response.success) {
        let updatedProject = response.project;
        
        // Now handle file uploads if any
        const uploads = [];
        let uploadSuccess = true;
        
        // Handle SOW upload if present
        if (formData.sowFile) {
          try {
            showSnackbar('Uploading project document...', 'info');
            const sowResponse = await uploadProjectDocument(formData.sowFile, projectId, 'SOW');
            if (sowResponse.success) {
              uploads.push('project document');
            } else {
              uploadSuccess = false;
              showSnackbar('Failed to upload project document', 'error');
            }
          } catch (uploadError) {
            uploadSuccess = false;
            console.error('Error uploading project document:', uploadError);
            showSnackbar('Error uploading project document', 'error');
          }
        }

        // Handle RFP upload if present
        if (formData.rfpFile) {
          try {
            showSnackbar('Uploading RFP document...', 'info');
            const rfpResponse = await uploadProjectDocument(formData.rfpFile, projectId, 'RFP');
            if (rfpResponse.success) {
              uploads.push('RFP document');
            } else {
              uploadSuccess = false;
              showSnackbar(rfpResponse.message || 'Failed to upload RFP document', 'error');
            }
          } catch (uploadError) {
            uploadSuccess = false;
            console.error('Error uploading RFP document:', uploadError);
            showSnackbar('Error uploading RFP document', 'error');
          }
        }
        
        // Handle additional documents if present
        if (formData.additionalFiles && formData.additionalFiles.length > 0) {
          for (const file of formData.additionalFiles) {
            try {
              const docResponse = await uploadProjectDocument(file, projectId, 'OTHER');
              if (docResponse.success) {
                uploads.push(file.name);
              } else {
                uploadSuccess = false;
                showSnackbar(`Failed to upload ${file.name}`, 'error');
              }
            } catch (uploadError) {
              uploadSuccess = false;
              console.error(`Error uploading ${file.name}:`, uploadError);
              showSnackbar(`Error uploading ${file.name}`, 'error');
            }
          }
        }
        
        // Fetch the latest project data with all documents
        await fetchProject();
        
        // Show success message
        if (uploads.length > 0 && uploadSuccess) {
          showSnackbar(`Project updated successfully with ${uploads.length} new document(s)`, 'success');
        } else if (uploads.length === 0) {
          showSnackbar('Project updated successfully', 'success');
        }
      } else {
        showSnackbar(response.message || 'Failed to update project', 'error');
      }
      
      handleCloseEditDialog();
    } catch (error) {
      console.error('Error updating project:', error);
      showSnackbar('An error occurred', 'error');
    }
  };
  
  const handleDeleteProject = async () => {
    if (window.confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
      try {
        const response = await deleteProject(projectId);
        if (response.success) {
          navigate('/projects');
          showSnackbar('Project deleted successfully', 'success');
        } else {
          showSnackbar(response.message || 'Failed to delete project', 'error');
        }
      } catch (error) {
        console.error('Error deleting project:', error);
        showSnackbar('An error occurred', 'error');
      }
    }
  };
  
  const handleOpenUserDialog = () => {
    setUserDialogOpen(true);
  };
  
  const handleCloseUserDialog = () => {
    setUserDialogOpen(false);
    setSelectedUserId('');
  };
  
  const handleAssignUser = async () => {
    if (!selectedUserId) return;
    
    try {
      // Make sure we pass an object with the user_id property
      const userData = { user_id: selectedUserId };
      const response = await assignUserToProject(projectId, userData);
      if (response.success) {
        showSnackbar('User assigned successfully', 'success');
        // Update the project state with the returned data if available
        if (response.project) {
          setProject(response.project);
        } else {
          fetchProject(); // Fall back to fetching if no project data returned
        }
      } else {
        showSnackbar(response.message || 'Failed to assign user', 'error');
      }
      handleCloseUserDialog();
    } catch (error) {
      console.error('Error assigning user:', error);
      showSnackbar('An error occurred', 'error');
    }
  };
  
  const handleRemoveUser = async (userId) => {
    if (window.confirm('Are you sure you want to remove this user from the project?')) {
      try {
        const response = await removeUserFromProject(projectId, userId);
        if (response.success) {
          showSnackbar('User removed successfully', 'success');
          // Update the project state with the returned data if available
          if (response.project) {
            setProject(response.project);
          } else {
            fetchProject(); // Fall back to fetching if no project data returned
          }
        } else {
          showSnackbar(response.message || 'Failed to remove user', 'error');
        }
      } catch (error) {
        console.error('Error removing user:', error);
        showSnackbar('An error occurred', 'error');
      }
    }
  };
  
  const handleUploadDocument = async (file, projectId, docType) => {
    try {
      const response = await uploadProjectDocument(file, projectId, docType);
      if (response.success) {
        showSnackbar('Document uploaded successfully', 'success');
        fetchProject(); // Refresh project to get updated document list
      } else {
        showSnackbar(response.message || 'Failed to upload document', 'error');
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      showSnackbar('An error occurred', 'error');
    }
  };
  
  const handleDeleteDocument = (document) => {
    if (!isAdmin) {
      showSnackbar('Only admins can delete documents', 'error');
      return;
    }
    setConfirmDialog({
      open: true,
      title: 'Delete Document',
      message: `Are you sure you want to permanently delete "${document.title}"? This cannot be undone.`,
      onConfirm: async () => {
        try {
          const response = await deleteProjectDocument(projectId, document.document_id);
          if (response.success) {
            showSnackbar('Document deleted successfully', 'success');
            fetchProject();
          } else {
            showSnackbar(response.error || 'Failed to delete document', 'error');
          }
        } catch (error) {
          console.error('Error deleting document:', error);
          showSnackbar('An error occurred', 'error');
        }
      }
    });
  };
  
  const handleDownloadDocument = async (document) => {
    try {
      // Placeholder for document download API call
      // const response = await downloadDocument(document.id);
      // ... handle download
      
      // For now, just show a success message
      showSnackbar('Document download feature is coming soon!', 'info');
    } catch (error) {
      console.error('Error downloading document:', error);
      showSnackbar('An error occurred', 'error');
    }
  };
  
  const handleOpenAiDialog = () => {
    setAiDialogOpen(true);
    setAiResult(null);
    setAiQuery('');
  };
  
  const handleCloseAiDialog = () => {
    setAiDialogOpen(false);
  };
  
  const handleProcessProject = async () => {
    try {
      setAiLoading(true);
      const result = await processProject(projectId);
      if (result.success) {
        showSnackbar('Project documents processed successfully for AI analysis', 'success');
      } else {
        showSnackbar(result.error || 'Failed to process project documents', 'error');
      }
    } catch (error) {
      console.error('Error processing project documents:', error);
      showSnackbar('An error occurred while processing documents', 'error');
    } finally {
      setAiLoading(false);
    }
  };
  
  const handleSubmitAiQuery = async () => {
    if (!aiQuery) return;
    
    try {
      setAiLoading(true);
      const result = await queryProject(projectId, aiQuery);
      setAiResult(result);
      if (!result.success) {
        showSnackbar(result.error || 'Failed to get answer from AI', 'error');
      }
    } catch (error) {
      console.error('Error querying project:', error);
      setAiResult({
        success: false,
        error: error.message || 'Failed to query project'
      });
      showSnackbar('An error occurred while querying AI', 'error');
    } finally {
      setAiLoading(false);
    }
  };
  
  const handleGenerateFromSOW = async (section) => {
    try {
      setAiLoading(true);
      const result = await generateFromSOW(projectId, section);
      if (result.success) {
        showSnackbar(`Content generated from project documents for ${section} section`, 'success');
        // Here you could do something with the generated content
        console.log('Generated content:', result.content);
      } else {
        showSnackbar(result.error || 'Failed to generate content from project documents', 'error');
      }
    } catch (error) {
      console.error('Error generating content from project documents:', error);
      showSnackbar('An error occurred', 'error');
    } finally {
      setAiLoading(false);
    }
  };
  
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };
  
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  
  // Filter out users who are already assigned to the project
  const unassignedUsers = availableUsers.filter(
    user => !project?.assigned_users?.some(assigned => assigned.id === user.id)
  );
  
  // Get project details for display
  const projectDetails = project ? [
    { label: 'Industry Vertical', value: project.industry_vertical },
    { label: 'Module Details', value: project.module_details },
    { label: 'Implementation Scope', value: project.implementation_scope },
    { 
      label: 'Created At', 
      value: new Date(project.created_at).toLocaleDateString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric'
      })
    }
  ] : [];
  
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }
  
  if (!project) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h5" color="error">
          Project not found
        </Typography>
        <Button 
          variant="text" 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate('/projects')}
          sx={{ mt: 2 }}
        >
          Back to Projects
        </Button>
      </Container>
    );
  }
  
  const isAdmin = currentUser && currentUser.role === 'admin';
  
  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton 
            onClick={() => navigate('/projects')} 
            aria-label="back"
            sx={{ mr: 1 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            {project.name}
          </Typography>
        </Box>
        
        <Box>
          <Button 
            variant="outlined" 
            startIcon={<SmartToyIcon />} 
            onClick={() => navigate(`/projects/${projectId}/insights`)}
            sx={{ mr: 1 }}
          >
            Project Insights
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<AutoAwesomeIcon />} 
            onClick={() => navigate(`/projects/${projectId}/prepare/scope`)}
            sx={{ mr: 1 }}
          >
            Prepare Phase
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<DescriptionIcon />} 
            onClick={() => navigate(`/projects/${projectId}/solution-proposal`)}
            sx={{ mr: 1 }}
          >
            Solution Proposal
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<ArchitectureIcon />} 
            onClick={() => navigate(`/projects/${projectId}/technical-specification`)}
            sx={{ mr: 1 }}
          >
            Technical Specs
          </Button>
          
          {isAdmin && (
            <>
              <Button 
                variant="outlined" 
                startIcon={<EditIcon />} 
                onClick={handleOpenEditDialog}
                sx={{ mr: 1 }}
              >
                Edit
              </Button>
              <Button 
                variant="outlined" 
                color="error" 
                startIcon={<DeleteIcon />}
                onClick={handleDeleteProject}
              >
                Delete
              </Button>
            </>
          )}
        </Box>
      </Box>
      
      {/* Description */}
      {project.description && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="body1">{project.description}</Typography>
        </Paper>
      )}
      
      {/* Tabs */}
      <Box sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleChangeTab} 
            aria-label="project tabs"
          >
            <Tab label="Overview" />
            <Tab label="Documents" />
            <Tab label="Users" />
            <Tab label="AI Actions" />
          </Tabs>
        </Box>
        
        {/* Overview Tab */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>Project Details</Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                  {projectDetails.map((detail, index) => (
                    detail.value && (
                      <Grid item xs={12} key={index}>
                        <Typography variant="subtitle2" color="text.secondary">
                          {detail.label}
                        </Typography>
                        <Typography variant="body1" paragraph>
                          {detail.value}
                        </Typography>
                      </Grid>
                    )
                  ))}
                </Grid>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Project Users</Typography>
                  {isAdmin && (
                    <Button 
                      variant="outlined" 
                      size="small" 
                      startIcon={<PersonAddIcon />}
                      onClick={handleOpenUserDialog}
                    >
                      Assign User
                    </Button>
                  )}
                </Box>
                <Divider sx={{ mb: 2 }} />
                
                {project.assigned_users && project.assigned_users.length > 0 ? (
                  <List>
                    {project.assigned_users.map((user) => (
                      <ListItem 
                        key={user.id}
                        secondaryAction={
                          isAdmin && (
                            <IconButton 
                              edge="end" 
                              aria-label="delete" 
                              onClick={() => handleRemoveUser(user.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          )
                        }
                      >
                        <ListItemAvatar>
                          <Avatar>{user.full_name ? user.full_name.charAt(0) : user.email.charAt(0)}</Avatar>
                        </ListItemAvatar>
                        <ListItemText 
                          primary={user.full_name || user.email}
                          secondary={user.role}
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body1" color="text.secondary">
                    No users assigned to this project yet.
                  </Typography>
                )}
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>
        
        {/* Documents Tab */}
        <TabPanel value={tabValue} index={1}>
          <ProjectDocumentsPanel
            project={project}
            onUploadDocument={handleUploadDocument}
            onDeleteDocument={handleDeleteDocument}
            onDownloadDocument={handleDownloadDocument}
            onRefresh={fetchProject}
            isAdmin={isAdmin}
          />
        </TabPanel>
        
        {/* Users Tab */}
        <TabPanel value={tabValue} index={2}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Project Users</Typography>
              {isAdmin && (
                <Button 
                  variant="contained" 
                  startIcon={<PersonAddIcon />}
                  onClick={handleOpenUserDialog}
                >
                  Assign User
                </Button>
              )}
            </Box>
            <Divider sx={{ mb: 2 }} />
            
            {project.assigned_users && project.assigned_users.length > 0 ? (
              <Grid container spacing={2}>
                {project.assigned_users.map((user) => (
                  <Grid item xs={12} sm={6} md={4} key={user.id}>
                    <Paper 
                      elevation={1} 
                      sx={{ 
                        p: 2, 
                        display: 'flex', 
                        flexDirection: 'column',
                        height: '100%',
                        position: 'relative'
                      }}
                    >
                      {isAdmin && (
                        <IconButton 
                          size="small" 
                          aria-label="delete" 
                          onClick={() => handleRemoveUser(user.id)}
                          sx={{ position: 'absolute', top: 8, right: 8 }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Avatar sx={{ mr: 2 }}>
                          {user.full_name ? user.full_name.charAt(0) : user.email.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="h6" noWrap>
                            {user.full_name || 'Unnamed User'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {user.email}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Chip 
                        label={user.role} 
                        color={user.role === 'admin' ? 'primary' : 'default'}
                        size="small"
                        sx={{ alignSelf: 'flex-start' }}
                      />
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No users assigned to this project yet.
              </Typography>
            )}
          </Paper>
        </TabPanel>
        
        {/* AI Actions Tab */}
        <TabPanel value={tabValue} index={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" gutterBottom>AI Actions</Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Box sx={{ mb: 3 }}>
                  <Button 
                    variant="contained" 
                    startIcon={<BiotechIcon />}
                    onClick={handleProcessProject}
                    fullWidth
                    disabled={aiLoading}
                  >
                    Process All Project Documents
                  </Button>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Process all documents in this project to enable AI querying. This might take a while for large documents.
                  </Typography>
                </Box>
                
                <Box sx={{ mb: 3 }}>
                  <Button 
                    variant="contained" 
                    startIcon={<SmartToyIcon />}
                    onClick={handleOpenAiDialog}
                    fullWidth
                    disabled={aiLoading}
                  >
                    Ask AI About This Project
                  </Button>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Query the AI with questions about this project based on all processed documents.
                  </Typography>
                </Box>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" gutterBottom>Generate From Project Documents</Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Typography variant="body2" paragraph>
                  Use AI to automatically generate functional specification content based on the project documents.
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Button 
                      variant="outlined" 
                      fullWidth
                      onClick={() => handleGenerateFromSOW('executive_summary')}
                      disabled={aiLoading}
                    >
                      Executive Summary
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Button 
                      variant="outlined" 
                      fullWidth
                      onClick={() => handleGenerateFromSOW('business_requirements')}
                      disabled={aiLoading}
                    >
                      Business Requirements
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Button 
                      variant="outlined" 
                      fullWidth
                      onClick={() => handleGenerateFromSOW('current_process')}
                      disabled={aiLoading}
                    >
                      Current Process
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Button 
                      variant="outlined" 
                      fullWidth
                      onClick={() => handleGenerateFromSOW('future_process')}
                      disabled={aiLoading}
                    >
                      Future Process
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>
      </Box>
      
      {/* Edit Project Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="md" fullWidth>
        <DialogTitle>Edit Project</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom sx={{ mt: 2, fontWeight: 'medium' }}>
                Project Details
              </Typography>
              <TextField
                autoFocus
                name="name"
                label="Project Name"
                value={formData.name}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
                required
              />
              <TextField
                name="description"
                label="Description"
                value={formData.description}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
                multiline
                rows={2}
              />
              <TextField
                name="industry_vertical"
                label="Industry Vertical"
                value={formData.industry_vertical}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
              />
              <TextField
                name="module_details"
                label="SAP Modules"
                value={formData.module_details}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
                helperText="Comma-separated list of SAP modules (e.g., 'FI, MM, SD')"
              />
              <TextField
                name="implementation_scope"
                label="Implementation Scope"
                value={formData.implementation_scope}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
                select
                SelectProps={{
                  native: true
                }}
              >
                <option value=""></option>
                <option value="Full implementation">Full implementation</option>
                <option value="Enhancement">Enhancement</option>
                <option value="Migration">Migration</option>
                <option value="Integration">Integration</option>
                <option value="Support">Support</option>
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom sx={{ mt: 2, fontWeight: 'medium' }}>
                Project Documents
              </Typography>

              {/* SOW */}
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Project Documents</Typography>
                {project.documents?.sow?.length > 0 && (
                  <List dense disablePadding sx={{ mb: 1 }}>
                    {project.documents.sow.map((doc) => (
                      <ListItem
                        key={doc.document_id}
                        sx={{ px: 0 }}
                        secondaryAction={
                          isAdmin && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setConfirmDialog({
                                open: true,
                                title: 'Delete Project Document',
                                message: `Are you sure you want to permanently delete "${doc.title}"? This cannot be undone.`,
                                onConfirm: async () => {
                                  const res = await deleteProjectDocument(projectId, doc.document_id);
                                  if (res.success) { showSnackbar('Project document deleted', 'success'); fetchProject(); handleCloseEditDialog(); }
                                  else showSnackbar(res.error || 'Failed to delete', 'error');
                                }
                              })}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )
                        }
                      >
                        <ListItemAvatar sx={{ minWidth: 32 }}>
                          <DescriptionIcon fontSize="small" color="primary" />
                        </ListItemAvatar>
                        <ListItemText
                          primary={doc.title}
                          primaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
                <input
                  type="file"
                  id="sow-upload"
                  accept=".pdf,.doc,.docx,.txt"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setFormData({ ...formData, sowFile: e.target.files[0] });
                    }
                  }}
                />
                <label htmlFor="sow-upload">
                  <Button variant="outlined" component="span" startIcon={<UploadIcon />} fullWidth size="small">
                    {project.documents?.sow?.length > 0 ? 'Replace Project Documents' : 'Upload Project Documents'}
                  </Button>
                </label>
                {formData.sowFile && (
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Selected: {formData.sowFile.name}
                  </Typography>
                )}
              </Paper>

              {/* RFP */}
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Request for Proposal (RFP)</Typography>
                {project.documents?.rfp?.length > 0 && (
                  <List dense disablePadding sx={{ mb: 1 }}>
                    {project.documents.rfp.map((doc) => (
                      <ListItem
                        key={doc.document_id}
                        sx={{ px: 0 }}
                        secondaryAction={
                          isAdmin && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setConfirmDialog({
                                open: true,
                                title: 'Delete RFP Document',
                                message: `Are you sure you want to permanently delete "${doc.title}"? This cannot be undone.`,
                                onConfirm: async () => {
                                  const res = await deleteProjectDocument(projectId, doc.document_id);
                                  if (res.success) { showSnackbar('RFP deleted', 'success'); fetchProject(); handleCloseEditDialog(); }
                                  else showSnackbar(res.error || 'Failed to delete', 'error');
                                }
                              })}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )
                        }
                      >
                        <ListItemAvatar sx={{ minWidth: 32 }}>
                          <DescriptionIcon fontSize="small" color="secondary" />
                        </ListItemAvatar>
                        <ListItemText
                          primary={doc.title}
                          primaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
                <input
                  type="file"
                  id="rfp-upload"
                  accept=".pdf,.doc,.docx,.txt"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setFormData({ ...formData, rfpFile: e.target.files[0] });
                    }
                  }}
                />
                <label htmlFor="rfp-upload">
                  <Button variant="outlined" component="span" startIcon={<UploadIcon />} fullWidth size="small">
                    {project.documents?.rfp?.length > 0 ? 'Replace RFP' : 'Upload RFP'}
                  </Button>
                </label>
                {formData.rfpFile && (
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Selected: {formData.rfpFile.name}
                  </Typography>
                )}
              </Paper>

              {/* Additional docs */}
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Additional Documents</Typography>
                <input
                  type="file"
                  id="additional-docs"
                  multiple
                  accept=".pdf,.doc,.docx,.txt"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    if (e.target.files && e.target.files.length > 0) {
                      setFormData({ ...formData, additionalFiles: Array.from(e.target.files) });
                    }
                  }}
                />
                <label htmlFor="additional-docs">
                  <Button variant="outlined" component="span" startIcon={<UploadIcon />} fullWidth size="small">
                    Add Documents
                  </Button>
                </label>
                {formData.additionalFiles && formData.additionalFiles.length > 0 && (
                  <Box mt={1}>
                    <Typography variant="caption" display="block">
                      Selected {formData.additionalFiles.length} file(s)
                    </Typography>
                    <List dense>
                      {formData.additionalFiles.map((file, index) => (
                        <ListItem key={index} sx={{ py: 0 }}>
                          <ListItemText
                            primary={file.name}
                            secondary={`${Math.round(file.size / 1024)} KB`}
                            primaryTypographyProps={{ variant: 'caption' }}
                            secondaryTypographyProps={{ variant: 'caption' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button onClick={handleSubmitEdit} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Assign User Dialog */}
      <Dialog open={userDialogOpen} onClose={handleCloseUserDialog}>
        <DialogTitle>Assign User to Project</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="user"
            label="Select User"
            select
            fullWidth
            variant="outlined"
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value)}
            SelectProps={{
              native: true
            }}
          >
            <option value="">Select a user</option>
            {unassignedUsers.map((user) => (
              <option key={user.id} value={user.id}>
                {user.full_name || user.email}
              </option>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUserDialog}>Cancel</Button>
          <Button 
            onClick={handleAssignUser} 
            variant="contained" 
            color="primary"
            disabled={!selectedUserId}
          >
            Assign User
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* AI Query Dialog */}
      <Dialog 
        open={aiDialogOpen} 
        onClose={handleCloseAiDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Ask AI About This Project</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="query"
            label="What would you like to know about this project?"
            type="text"
            fullWidth
            variant="outlined"
            value={aiQuery}
            onChange={(e) => setAiQuery(e.target.value)}
            multiline
            rows={2}
            sx={{ mb: 2 }}
          />
          
          {aiLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          )}
          
          {aiResult && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="h6">AI Response:</Typography>
              {aiResult.success ? (
                <>
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      p: 2, 
                      bgcolor: 'action.hover',
                      mt: 2,
                      borderRadius: 1,
                      whiteSpace: 'pre-wrap'
                    }}
                  >
                    <Typography variant="body1">
                      {aiResult.answer}
                    </Typography>
                  </Paper>
                  
                  {aiResult.sources && aiResult.sources.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle1">Sources:</Typography>
                      <List dense>
                        {aiResult.sources.map((source, index) => (
                          <ListItem key={index} sx={{ p: 1 }}>
                            <ListItemText 
                              primary={source.content} 
                              secondary={source.metadata?.document_name || 'Document fragment'}
                              primaryTypographyProps={{ 
                                variant: 'body2', 
                                sx: { fontStyle: 'italic' } 
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </>
              ) : (
                <Typography color="error">
                  {aiResult.error || 'Failed to get an answer. Please try a different question.'}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAiDialog}>Close</Button>
          <Button 
            onClick={handleSubmitAiQuery} 
            variant="contained" 
            disabled={!aiQuery || aiLoading}
          >
            Ask AI
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ ...confirmDialog, open: false })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon color="error" />
          {confirmDialog.title}
        </DialogTitle>
        <DialogContent>
          <Typography>{confirmDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={async () => {
              setConfirmDialog({ ...confirmDialog, open: false });
              if (confirmDialog.onConfirm) await confirmDialog.onConfirm();
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProjectDetailPage;
